import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CASCINA_CENTER, CASCINA_ZOOM, STATUS_CONFIG } from "../lib/constants";

// Fix default marker icons for Leaflet in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Map behavior constants
const MAP_FIT_MAX_ZOOM = 15;
const MAP_FIT_PADDING_PX = 40;
const MAP_INVALIDATE_SIZE_DELAY_MS = 100;
const MARKER_CIRCLE_RADIUS = 10;

// Fits map bounds to markers whenever the marker set changes.
// Also runs invalidateSize() so the map redraws correctly when the parent
// container (e.g. a Dialog) opens after the map has mounted.
// Uses a stable signature (join of lat,lng) so pan/zoom is not reset on every parent render.
const FitBoundsAndInvalidate = ({ points }) => {
  const map = useMap();
  const signature = points.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    // Trigger a resize so Leaflet recalculates its size within lazy containers
    const t = setTimeout(() => map.invalidateSize(), MAP_INVALIDATE_SIZE_DELAY_MS);
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [MAP_FIT_PADDING_PX, MAP_FIT_PADDING_PX], maxZoom: MAP_FIT_MAX_ZOOM });
    }
    return () => clearTimeout(t);
    // We intentionally depend on `signature` (a stable string) instead of `points`
    // to avoid resetting user pan/zoom on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);
  return null;
};

const STATUS_COLORS = {
  in_attesa: "#f59e0b",
  confermato: "#3b82f6",
  completato: "#10b981",
  annullato: "#ef4444",
};

// Build a numbered DivIcon (used for "giro consegne" mode)
const numberedIcon = (num, color = "#0284C7") =>
  L.divIcon({
    className: "numbered-marker",
    html: `<div style="
        background:${color};
        color:white;
        width:32px;height:32px;
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-weight:700;font-size:14px;
        border:3px solid white;
        box-shadow:0 2px 6px rgba(0,0,0,0.3);
      ">${num}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });

export const DeliveriesMap = ({
  bookings = [],
  height = 500,
  showHeatLegend = true,
  numbered = false,
  showRoute = false,
}) => {
  const validBookings = bookings.filter(
    (b) => typeof b.lat === "number" && typeof b.lng === "number"
  );

  // For numbered mode, sort by ora_consegna ascending
  const orderedBookings = numbered
    ? [...validBookings].sort((a, b) => (a.ora_consegna || "").localeCompare(b.ora_consegna || ""))
    : validBookings;

  const routePositions = orderedBookings.map((b) => [b.lat, b.lng]);

  return (
    <div className="relative">
      <MapContainer
        center={CASCINA_CENTER}
        zoom={CASCINA_ZOOM}
        style={{ height: `${height}px`, width: "100%", borderRadius: "1rem" }}
        scrollWheelZoom={true}
        data-testid="deliveries-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBoundsAndInvalidate points={orderedBookings} />

        {showRoute && routePositions.length >= 2 && (
          <Polyline
            positions={routePositions}
            pathOptions={{ color: "#0284C7", weight: 3, opacity: 0.6, dashArray: "8 6" }}
          />
        )}

        {orderedBookings.map((b, idx) =>
          numbered ? (
            <Marker
              key={b.id}
              position={[b.lat, b.lng]}
              icon={numberedIcon(idx + 1, STATUS_COLORS[b.stato] || "#0284C7")}
            >
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <strong>#{idx + 1} - {b.nome}</strong>
                  <br />
                  <span style={{ fontSize: 12 }}>{b.indirizzo}</span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    {b.data_consegna} - <strong>{b.ora_consegna}</strong>
                  </span>
                  <br />
                  <span style={{ fontSize: 12, textTransform: "capitalize" }}>
                    {b.tipo_acqua} · {STATUS_CONFIG[b.stato]?.label || b.stato}
                  </span>
                </div>
              </Popup>
            </Marker>
          ) : (
            <CircleMarker
              key={b.id}
              center={[b.lat, b.lng]}
              radius={MARKER_CIRCLE_RADIUS}
              pathOptions={{
                color: STATUS_COLORS[b.stato] || "#64748b",
                fillColor: STATUS_COLORS[b.stato] || "#64748b",
                fillOpacity: 0.7,
                weight: 2,
              }}
            >
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <strong>{b.nome}</strong>
                  <br />
                  <span style={{ fontSize: 12 }}>{b.indirizzo}</span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    {b.data_consegna} - {b.ora_consegna}
                  </span>
                  <br />
                  <span style={{ fontSize: 12, textTransform: "capitalize" }}>
                    {b.tipo_acqua} · {STATUS_CONFIG[b.stato]?.label || b.stato}
                  </span>
                </div>
              </Popup>
            </CircleMarker>
          )
        )}
      </MapContainer>

      {showHeatLegend && (
        <div
          className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-slate-200 z-[1000]"
          data-testid="map-legend"
        >
          <p className="text-xs font-semibold text-slate-700 mb-2">Stato Consegna</p>
          <div className="space-y-1">
            {Object.entries(STATUS_COLORS).map(([key, color]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: color }} />
                <span className="text-xs text-slate-600">
                  {STATUS_CONFIG[key]?.label || key}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {validBookings.length === 0 && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]"
          data-testid="map-empty"
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-md border border-slate-200">
            <p className="text-sm text-slate-600">Nessuna consegna geolocalizzata in questo periodo</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Build a Google Maps directions URL for a list of geolocated bookings,
 * ordered by ora_consegna ascending. Returns null if no usable points.
 *
 * Format used: https://www.google.com/maps/dir/?api=1&origin=...&destination=...&waypoints=...|...
 */
export const buildGoogleMapsRouteUrl = (bookings = []) => {
  const points = [...bookings]
    .filter((b) => typeof b.lat === "number" && typeof b.lng === "number")
    .sort((a, b) => (a.ora_consegna || "").localeCompare(b.ora_consegna || ""));

  if (points.length === 0) return null;

  if (points.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${points[0].lat},${points[0].lng}&travelmode=driving`;
  }

  const origin = points[0];
  const destination = points[points.length - 1];
  const waypoints = points.slice(1, -1);

  const params = new URLSearchParams({
    api: "1",
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    travelmode: "driving",
  });
  if (waypoints.length > 0) {
    params.set("waypoints", waypoints.map((p) => `${p.lat},${p.lng}`).join("|"));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};
